import { useEffect, useRef } from 'react';
import { useUploadStore } from '../../store/upload.store';
import { SocketKey } from '@/Config/api/socketKey';
import { useSocket } from '@/Domain/components/SocketProvider';
import { UploadStatusEnum } from '@/Domain/enum/UploadStatus.enum';

type ArticleStateChangeSocket = {
  article_id: number;
  state: string;
  total_items?: number;
  visited_items?: number;
  current_url?: string;
  error_message?: string;
  capped_at_limit?: boolean;
};

type Props = {
  onSuccess?: () => void;
};

const TERMINAL_STATUSES: readonly UploadStatusEnum[] = [
  UploadStatusEnum.DONE,
  UploadStatusEnum.ERROR,
];

export const useArticleStateChangeSocket = ({ onSuccess }: Props) => {
  const {
    updateItemStatus,
    updateScrapingProgress,
    addArticleMapping,
    getItemIdByArticleId,
    getUnmappedPendingFiles,
  } = useUploadStore();
  const { socket } = useSocket();

  const processedArticleIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const handleArticleStateChanged = (data: ArticleStateChangeSocket) => {
      const articleId = data.article_id;

      let status: UploadStatusEnum;
      switch (data.state as UploadStatusEnum) {
        case UploadStatusEnum.ERROR:
          status = UploadStatusEnum.ERROR;
          break;
        case UploadStatusEnum.DONE:
        case UploadStatusEnum.DONE_FALLBACK:
        case UploadStatusEnum.META_ARTICLE_DELETED:
          status = UploadStatusEnum.DONE;
          if (onSuccess) {
            onSuccess();
          }
          break;
        default:
          status = UploadStatusEnum.PENDING;
      }

      const errorMessage = data.error_message;
      const fileId = getItemIdByArticleId(articleId);

      // Shared progress-application helper so the mapped and unmapped
      // branches stay in lockstep. Without this, the very first event
      // that also establishes the article → file mapping would drop its
      // `total_items` / `visited_items` payload — visible when the
      // backend's upfront `SCRAPING_PING(0/N)` seed arrives before any
      // mapping exists (common in sitewide / sitemap / multiple-pages
      // mode under the unified progress protocol).
      // Merge-style update so a terminal META_ARTICLE_DELETED that carries
      // capped_at_limit but no counts doesn't clobber the live counter.
      const applyProgress = (targetFileId: number): void => {
        const update: Partial<{
          visitedItems: number;
          totalItems: number;
          currentUrl: string;
          cappedAtLimit: boolean;
        }> = {};
        if (data.total_items !== undefined && data.visited_items !== undefined) {
          update.totalItems = data.total_items;
          update.visitedItems = data.visited_items;
          update.currentUrl = data.current_url ?? '';
        }
        if (data.capped_at_limit) {
          update.cappedAtLimit = true;
        }
        if (Object.keys(update).length > 0) {
          updateScrapingProgress(targetFileId, update);
        }
      };

      // Once a file has reached a terminal state, refuse to demote it back to
      // a non-terminal one — defense-in-depth against late SCRAPING / SCRAPING_PING
      // events that slip past the backend guard. Read `files` from the store
      // directly (not the hook closure) so back-to-back events in a burst see
      // the post-update state from the prior event in the same tick.
      const applyStatus = (targetFileId: number) => {
        const current = useUploadStore.getState().files.find((f) => f.id === targetFileId);
        if (
          current &&
          TERMINAL_STATUSES.includes(current.status) &&
          !TERMINAL_STATUSES.includes(status)
        ) {
          return;
        }
        updateItemStatus(targetFileId, status, errorMessage);
      };

      if (fileId) {
        applyStatus(fileId);
        applyProgress(fileId);
        processedArticleIdsRef.current.add(articleId);
      } else if (!processedArticleIdsRef.current.has(articleId)) {
        const unmappedFiles = getUnmappedPendingFiles();

        if (unmappedFiles.length > 0) {
          const unmappedFile = unmappedFiles[0];
          addArticleMapping(articleId, unmappedFile.id);
          processedArticleIdsRef.current.add(articleId);
          applyStatus(unmappedFile.id);
          applyProgress(unmappedFile.id);
        }
      }
    };

    socket.on(SocketKey.knowledgeBase.articleStateChanged, handleArticleStateChanged);

    return () => {
      socket.off(SocketKey.knowledgeBase.articleStateChanged, handleArticleStateChanged);
    };
  }, [
    updateItemStatus,
    updateScrapingProgress,
    getItemIdByArticleId,
    addArticleMapping,
    getUnmappedPendingFiles,
    onSuccess,
    socket,
  ]);
};
